import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { Observable, from, throwError } from 'rxjs';
import { map, catchError, first, tap, switchMap } from 'rxjs/operators';
import { Router } from '@angular/router';
import { ReturnResult } from '../models/return-result';
import { KeycloakModel } from '../models/key-cloak.model';
import { SettingService } from './setting.service';

@Injectable({
  providedIn: 'root'
})
export class KeycloakService {
  private keycloakAuth: any;
  private refreshTokenInterval: any = null;
  private userProfile: any = null;
  private keycloakConfig: KeycloakModel = null;

  constructor(
    private http: HttpClient,
    private router: Router,
    private settingService: SettingService
  ) {}

  async initKeycloak(): Promise<any> {
    try {
      this.keycloakConfig = await this.getKeycloakConfig();
      
      if (!this.isValidConfig(this.keycloakConfig)) {
        return Promise.resolve(false);
      }
      
      const Keycloak = (window as any).Keycloak;
      this.keycloakAuth = new Keycloak({
        url: this.keycloakConfig?.url,
        realm: this.keycloakConfig?.realm, 
        clientId: this.keycloakConfig?.clientId
      });
      
      return this.init();
    } catch (error) {
      console.error('Failed to initialize Keycloak with settings', error);
      return Promise.resolve(false);
    }
  }

  private init(): Promise<any> {
    return new Promise((resolve, reject) => {
      if (!this.keycloakAuth) {
        reject('Keycloak not initialized, call initKeycloak() first');
        return;
      }
      
      this.keycloakAuth.init({
        onLoad: 'check-sso',
        silentCheckSsoRedirectUri: window.location.origin + '/assets/silent-check-sso.html',
        checkLoginIframe: true,
        checkLoginIframeInterval: 10,
        messageReceiveTimeout: 10000,
        silentCheckSsoTimeout: 10000
      })
      .then((authenticated: boolean) => {
        if (authenticated) {
          this.startTokenRefreshInterval();
          this.loadUserProfile();
          this.setupTokenHandlers();
        }
        resolve(authenticated);
      })
      .catch((error: any) => {
        if (this.isTimeoutError(error)) {
          resolve(false);
        } else {
          reject(error);
        }
      });
    });
  }

  async login(options?: any): Promise<void> {
    const returnUrl = this.router.url !== '/auth' ? this.router.url : '/';
    localStorage.setItem('keycloak_return_url', returnUrl);

    try {
      if (!this.keycloakConfig) {
        this.keycloakConfig = await this.getKeycloakConfig();
      }
      
      if (!this.isValidConfig(this.keycloakConfig)) {
        throw new Error('Invalid Keycloak configuration');
      }
      
      const redirectUri = window.location.origin + '/auth/callback';
      const redirectUrl = this.buildAuthUrl(redirectUri);
      
      window.location.href = redirectUrl;
      return Promise.resolve();
    } catch (error) {
      if (this.keycloakAuth) {
        return this.keycloakAuth.login(options || {
          redirectUri: window.location.origin + '/auth/callback',
          responseType: 'code'
        });
      } else {
        return Promise.reject('Keycloak not initialized');
      }
    }
  }

  logout(): Promise<void> {
    this.cleanup();
    
    setTimeout(() => {
      window.location.href = window.location.origin + '/auth/login';
    }, 100);
    
    return this.keycloakAuth.logout({
      redirectUri: window.location.origin + '/auth/login'
    });
  }

  getToken(): Promise<string> {
    return new Promise<string>((resolve, reject) => {
      if (!this.keycloakAuth) {
        reject('Keycloak not initialized');
        return;
      }
      
      if (this.keycloakAuth.token) {
        this.keycloakAuth.updateToken(5)
          .then(() => resolve(this.keycloakAuth.token))
          .catch(() => {
            this.keycloakAuth.clearToken();
            reject('Token expired and cannot be refreshed');
          });
      } else {
        reject('No token available');
      }
    });
  }
  
  isLoggedIn(): Promise<boolean> {
    return new Promise<boolean>((resolve) => {
      if (!this.keycloakAuth) {
        resolve(false);
        return;
      }
      
      this.keycloakAuth.updateToken(5)
        .then(() => resolve(true))
        .catch(() => resolve(false));
    });
  }

  exchangeTokenForJWT(): Observable<ReturnResult<any>> {
    return from(this.getToken()).pipe(
      switchMap(token => {
        const apiUrl = `${environment.apiAuth}/KeycloakLogin`;
        const headers = new HttpHeaders()
          .append('Authorization', `Bearer ${token}`)
          .append('Content-Type', 'application/json');
        
        return this.http.get<ReturnResult<any>>(apiUrl, { headers });
      }),
      first(),
      catchError(error => throwError(() => error))
    );
  }

  handleCallback(token: string): Observable<any> {
    const apiUrl = `${environment.apiAuth}/KeycloakCallback`;
    const headers = new HttpHeaders()
      .set('Content-Type', 'application/json')
      .set('Authorization', `Bearer ${token}`);
    
    return this.http.post<any>(apiUrl, {}, { headers }).pipe(
      first(),
      catchError(error => throwError(() => error))
    );
  }

  async getKeycloakConfiguration(): Promise<KeycloakModel> {
    return await this.getKeycloakConfig();
  }

  getKeycloakInstance(): any {
    return this.keycloakAuth;
  }

  getUserProfile(): any {
    return this.userProfile;
  }

  // Private helper methods
  private async getKeycloakConfig(): Promise<KeycloakModel> {
    try {
      const resp = await this.settingService.getSettingByKeyAndGroup("KEYCLOAK_CONFIG", "SYSTEM").toPromise();
      if (resp?.result && resp.result?.value) {
        return JSON.parse(resp.result.value) as KeycloakModel;
      }
      return null;
    } catch (error) {
      return null;
    }
  }

  private isValidConfig(config: KeycloakModel): boolean {
    return !!(config && config.url && config.realm && config.clientId);
  }

  private buildAuthUrl(redirectUri: string): string {
    return `${this.keycloakConfig.url}/realms/${this.keycloakConfig.realm}/protocol/openid-connect/auth?client_id=${this.keycloakConfig.clientId}&redirect_uri=${encodeURIComponent(redirectUri)}&response_type=code&scope=openid`;
  }

  private setupTokenHandlers(): void {
    this.keycloakAuth.onTokenExpired = () => {
      this.keycloakAuth.updateToken(30).catch(() => {
        this.handleTokenExpired();
      });
    };
    
    this.keycloakAuth.onAuthLogout = () => {
      this.handleTokenExpired();
    };
  }

  private startTokenRefreshInterval(): void {
    this.clearTokenRefreshInterval();
    this.refreshTokenInterval = setInterval(() => {
      if (this.keycloakAuth) {
        this.keycloakAuth.updateToken(60).catch(() => {
          this.clearTokenRefreshInterval();
          this.keycloakAuth.clearToken();
        });
      }
    }, 60000);
  }

  private clearTokenRefreshInterval(): void {
    if (this.refreshTokenInterval) {
      clearInterval(this.refreshTokenInterval);
      this.refreshTokenInterval = null;
    }
  }

  private loadUserProfile(): Promise<any> {
    return new Promise((resolve, reject) => {
      if (!this.keycloakAuth) {
        reject('Keycloak not initialized');
        return;
      }
      
      this.keycloakAuth.loadUserProfile()
        .then((profile: any) => {
          this.userProfile = profile;
          resolve(profile);
        })
        .catch((error: any) => reject(error));
    });
  }

  private handleTokenExpired(): void {
    localStorage.removeItem('auth_app_token');
    localStorage.removeItem('accessToken');
    
    if (this.keycloakAuth) {
      this.keycloakAuth.clearToken();
    }
    
    this.clearTokenRefreshInterval();
    window.location.href = window.location.origin + '/auth/login';
  }

  private cleanup(): void {
    this.clearTokenRefreshInterval();
    localStorage.removeItem('auth_app_token');
    localStorage.removeItem('accessToken');
  }

  private isTimeoutError(error: any): boolean {
    return error && typeof error === 'object' && error.error && 
           error.error.includes('Timeout when waiting for 3rd party check iframe message');
  }
} 